import { useCallback, useState, useRef } from 'react'
import {
  UseFileUploadReturn,
  FileUploadResult,
  FileUploadProgress,
  FileUploadStatus,
  FileValidationResult,
} from '../types'
import {
  validateFile,
  createUploadTicketRequest,
  generateUploadTicket,
  uploadFileToS3,
  notifyUploadComplete,
  extractFileIdFromKey,
} from '../utils/file-upload-api'
import { useFileUploadConfig } from './useFileUploadConfig'

interface UseFileUploadOptions {
  projectId?: number
  urbanAnalysisId?: number
  userId?: number
  userRole?: string
  onUploadStart?: (file: File) => void
  onUploadProgress?: (progress: FileUploadProgress) => void
  onUploadComplete?: (result: FileUploadResult) => void
  onUploadError?: (error: string, file?: File) => void
}

/**
 * Custom hook for handling file uploads with backend-driven configuration
 */
export function useFileUpload(options: UseFileUploadOptions): UseFileUploadReturn {
  const { projectId, urbanAnalysisId, userId, userRole, onUploadStart, onUploadProgress, onUploadComplete, onUploadError } = options

  // State management
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<Record<string, FileUploadProgress>>({})
  const [errors, setErrors] = useState<string[]>([])

  // Refs for tracking active uploads
  const activeUploadsRef = useRef<Set<string>>(new Set())

  // Get configuration from backend
  const {
    configuration,
    isLoading: isLoadingConfig,
    refreshConfig,
    getUsageMetadata,
  } = useFileUploadConfig()

  /**
   * Updates progress for a specific file upload
   */
  const updateProgress = useCallback((fileId: string, update: Partial<FileUploadProgress>) => {
    setUploadProgress(prev => ({
      ...prev,
      [fileId]: { ...prev[fileId], ...update } as FileUploadProgress,
    }))

    const updatedProgress = { ...uploadProgress[fileId], ...update } as FileUploadProgress
    if (onUploadProgress) {
      onUploadProgress(updatedProgress)
    }
  }, [uploadProgress, onUploadProgress])

  /**
   * Adds an error to the error list
   */
  const addError = useCallback((error: string, file?: File) => {
    setErrors(prev => [...prev, error])
    if (onUploadError) {
      onUploadError(error, file)
    }
  }, [onUploadError])

  /**
   * Clears all errors
   */
  const clearErrors = useCallback(() => {
    setErrors([])
  }, [])

  /**
   * Validates a file against backend configuration
   */
  const validateFileWithConfig = useCallback((file: File, usage: string | undefined): FileValidationResult => {
    if (!configuration) {
      return {
        isValid: false,
        errors: ['Configuration non disponible'],
        warnings: [],
      }
    }
    return validateFile(file, usage, configuration)
  }, [configuration])

  /**
   * Uploads a single file
   */
  const uploadFile = useCallback(async (
    file: File,
    usage?: string
  ): Promise<FileUploadResult> => {
    const fileId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

    try {
      // Check if configuration is available
      if (!configuration) {
        const errorMessage = 'Configuration de téléchargement non disponible'
        addError(errorMessage, file)
        return {
          success: false,
          filename: file.name,
          error: errorMessage,
        }
      }

      // Validate file first
      const validation = validateFileWithConfig(file, usage)
      if (!validation.isValid) {
        const errorMessage = validation.errors.join(', ')
        addError(errorMessage, file)
        return {
          success: false,
          filename: file.name,
          error: errorMessage,
        }
      }

      // Initialize progress tracking
      const initialProgress: FileUploadProgress = {
        fileId,
        filename: file.name,
        status: FileUploadStatus.VALIDATING,
        progress: 0,
        totalSize: file.size,
        uploadedSize: 0,
      }

      setUploadProgress(prev => ({ ...prev, [fileId]: initialProgress }))
      activeUploadsRef.current.add(fileId)

      if (onUploadStart) {
        onUploadStart(file)
      }

      // Update uploading state
      setIsUploading(true)

      // Step 1: Generate upload ticket
      updateProgress(fileId, {
        status: FileUploadStatus.GENERATING_TICKET,
        progress: 10,
      })

      const usageMetadata = usage ? getUsageMetadata(usage) : null
      // No usage (unassigned) or the UNDEFINED catch-all carry no section.
      const section =
        !usage || usage === 'UNDEFINED' ? undefined : usageMetadata?.section

      const ticketRequest = createUploadTicketRequest(projectId, file, usage, section, configuration, urbanAnalysisId)

      // Use provided user info or get from context
      const currentUserId = userId || parseInt(localStorage.getItem('userId') || '0')
      const currentUserRole = userRole || localStorage.getItem('userRole') || 'USER'

      const ticket = await generateUploadTicket(ticketRequest, currentUserId, currentUserRole)

      // Step 2: Upload file to S3
      updateProgress(fileId, {
        status: FileUploadStatus.UPLOADING,
        progress: 20,
      })

      await uploadFileToS3(file, ticket.uploadUrl, (progress) => {
        const adjustedProgress = 20 + (progress * 0.6) // Progress from 20% to 80%
        updateProgress(fileId, {
          progress: adjustedProgress,
          uploadedSize: Math.floor((progress / 100) * file.size),
        })
      })

      // Step 3: Notify backend of completion
      updateProgress(fileId, {
        status: FileUploadStatus.PROCESSING,
        progress: 85,
      })

      const actualFileId = extractFileIdFromKey(ticket.fileKey)

      const { archiveJobId } = await notifyUploadComplete(
        {
          fileId: actualFileId,
          fileKey: ticket.fileKey,
          token: ticket.token,
          actualSize: file.size,
        },
        currentUserId,
        currentUserRole
      )

      // Complete
      updateProgress(fileId, {
        status: FileUploadStatus.COMPLETE,
        progress: 100,
        uploadedSize: file.size,
      })

      const result: FileUploadResult = {
        success: true,
        fileId: actualFileId,
        filename: file.name,
        progress: uploadProgress[fileId],
        archiveJobId,
      }

      if (onUploadComplete) {
        onUploadComplete(result)
      }

      return result

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue lors du téléchargement'

      updateProgress(fileId, {
        status: FileUploadStatus.ERROR,
        error: errorMessage,
      })

      addError(errorMessage, file)

      return {
        success: false,
        filename: file.name,
        error: errorMessage,
        progress: uploadProgress[fileId],
      }
    } finally {
      activeUploadsRef.current.delete(fileId)

      // Update uploading state if no more active uploads
      if (activeUploadsRef.current.size === 0) {
        setIsUploading(false)
      }
    }
  }, [
    projectId,
    userId,
    userRole,
    configuration,
    validateFileWithConfig,
    updateProgress,
    addError,
    onUploadStart,
    onUploadComplete,
    uploadProgress,
    getUsageMetadata,
  ])

  /**
   * Uploads multiple files sequentially
   */
  const uploadFiles = useCallback(async (
    files: File[],
    usage?: string
  ): Promise<FileUploadResult[]> => {
    const results: FileUploadResult[] = []

    for (const file of files) {
      const result = await uploadFile(file, usage)
      results.push(result)

      // Small delay between uploads to prevent overwhelming the server
      if (files.length > 1) {
        await new Promise(resolve => setTimeout(resolve, 100))
      }
    }

    return results
  }, [uploadFile])

  return {
    uploadFile,
    uploadFiles,
    validateFile: validateFileWithConfig,
    isUploading,
    uploadProgress,
    errors,
    clearErrors,
    configuration,
    isLoadingConfig,
    refreshConfig,
  }
}
